import React from 'react';
import PropTypes from 'prop-types';
import './css/OrderPerson.css';
import { PERSON_PASSPORT } from '../../Utils/Constants/GlobalConstants';
import {
  EMAIL,
  PHONE,
  MALE_SEX,
  FEMALE_SEX,
  PASSPORT_DATE_START,
  PASSPORT_DATE_END,
  PASSPORT_IDENTIFICATION,
  CONTACT_INFO,
  POSTAL_ADDRESS,
  HOUSE_SHORT,
  FLAT_SHORT,
} from '../../Utils/Constants/CheckoutConstants';

const sexTexts = {
  male: MALE_SEX,
  female: FEMALE_SEX,
};

const joinFilled = (items, separator) =>
  items.filter((item) => item).join(separator);

// dates are saved as 12-12-1990
const getDateText = (date) => (date ? `${date}`.replace(/[-/]/g, '.') : '');

function OrderPerson(props) {
  const { title, person, email, phone, action } = props;

  const fullName = joinFilled(
    [person.lastName, person.firstName, person.afterName],
    ' ',
  );
  const latinName = joinFilled(
    [person.lastNameUpper, person.firstNameUpper],
    ' ',
  );
  const name = fullName || latinName;
  const initials = joinFilled(
    name.split(' ').slice(0, 2).map((part) => part.charAt(0)),
    '',
  ).toUpperCase();

  const address = person.city
    ? joinFilled(
      [
        person.postIndex,
        person.city,
        person.street,
        person.house && `${HOUSE_SHORT} ${person.house}`,
        person.flat && `${FLAT_SHORT} ${person.flat}`,
      ],
      ', ',
    )
    : '';

  const tags = [
    sexTexts[person.sex] || person.sex,
    getDateText(person.birthDay),
    person.citizenship,
  ].filter((item) => item);

  const passportRows = [
    { label: PASSPORT_DATE_START, value: getDateText(person.passportStartDate) },
    { label: PASSPORT_DATE_END, value: getDateText(person.passportEndDate) },
    // long value: goes under the label to stay on one line
    { label: PASSPORT_IDENTIFICATION, value: person.identity, isWide: true },
  ].filter((row) => row.value);

  const contacts = [
    { label: EMAIL, value: email },
    { label: PHONE, value: phone },
  ].filter((row) => row.value);

  const renderLine = (row) => (
    <div
      key={`opr-line-${row.label}`}
      className={`opr_line ${row.isWide ? 'opr_line_wide' : ''}`}
    >
      <div className="gr_text_13">{row.label}</div>
      <div className="br_text_14 opr_line_value">{row.value}</div>
    </div>
  );

  const renderPassport = () =>
    (person.passport || passportRows.length > 0) && (
      <div className="opr_block opr_passport">
        <div className="opr_passport_head">
          <span className="wm_text_14">{PERSON_PASSPORT}</span>
          {person.passport && (
            <span className="opr_passport_number">{person.passport}</span>
          )}
        </div>
        {passportRows.length > 0 && (
          <div className="opr_block_body">{passportRows.map(renderLine)}</div>
        )}
      </div>
    );

  const renderContacts = () =>
    contacts.length > 0 && (
      <div className="opr_block">
        <div className="bm_text_14 mb_8">{CONTACT_INFO}</div>
        {contacts.map(renderLine)}
      </div>
    );

  const renderAddress = () =>
    address && (
      <div className="opr_block">
        <div className="bm_text_14 mb_8">{POSTAL_ADDRESS}</div>
        <div className="br_text_14">{address}</div>
      </div>
    );

  return (
    <div className="opr_card">
      <div className="opr_head">
        <div className="opr_avatar">{initials}</div>
        <div className="opr_name_w">
          <div className="gr_text_13">{title}</div>
          <div className="bm_text_18 opr_name">{name}</div>
          {fullName && latinName && (
            <div className="gr_text_14 opr_name">{latinName}</div>
          )}
        </div>
        {action && <div className="opr_action">{action}</div>}
      </div>

      {tags.length > 0 && (
        <div className="opr_tags">
          {tags.map((tag) => (
            <span key={`opr-tag-${tag}`} className="opr_tag br_text_14">
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="opr_blocks">
        {renderPassport()}
        {renderContacts()}
        {renderAddress()}
      </div>
    </div>
  );
}

OrderPerson.propTypes = {
  title: PropTypes.string.isRequired,
  person: PropTypes.instanceOf(Object).isRequired,
  email: PropTypes.string,
  phone: PropTypes.string,
  action: PropTypes.node,
};

OrderPerson.defaultProps = {
  email: '',
  phone: '',
  action: null,
};

export default OrderPerson;
